import React, { useState, useEffect } from 'react';
import { Footprints, TrendingDown, TrendingUp, CheckCircle, AlertTriangle, Info } from 'lucide-react';

export default function WalkImpactCard() {
  const [impact, setImpact] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('gym_notebook_auth_token');
    if (!token) return;

    fetch(`${API_URL}/api/v2/walk-trigger/yesterday-impact`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.has_data) setImpact(data);
      })
      .catch(() => {});
  }, []);

  if (!impact || dismissed) return null;

  const icon = {
    success: <CheckCircle className="h-4 w-4 text-green-400" />,
    good: <TrendingDown className="h-4 w-4 text-blue-400" />,
    suggestion: <AlertTriangle className="h-4 w-4 text-amber-400" />,
    info: <Info className="h-4 w-4 text-slate-400" />,
  }[impact.feedback_type] || <Info className="h-4 w-4 text-slate-400" />;

  const borderColor = {
    success: 'border-green-500/30',
    good: 'border-blue-500/30',
    suggestion: 'border-amber-500/30',
    info: 'border-slate-600',
  }[impact.feedback_type] || 'border-slate-600';

  return (
    <div className={`bg-slate-800/60 border ${borderColor} rounded-xl p-3 mb-3`} data-testid="walk-impact-card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 mb-1.5">
          <Footprints className="h-4 w-4 text-amber-400" />
          <span className="text-xs font-medium text-slate-300">Yesterday's Walk Impact</span>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-slate-500 hover:text-slate-300 text-xs"
          data-testid="dismiss-impact"
        >
          Dismiss
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="text-center">
          <p className="text-[10px] text-slate-500">High-Carb Meals</p>
          <p className="text-sm font-bold text-white">{impact.high_carb_meals.length}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-slate-500">Steps</p>
          <p className="text-sm font-bold text-white">{impact.steps.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-slate-500">Post-Meal In Range</p>
          <p className={`text-sm font-bold ${impact.out_range === 0 ? 'text-green-400' : 'text-amber-400'}`}>
            {impact.after_meal_readings > 0 ? `${impact.in_range}/${impact.after_meal_readings}` : '—'}
          </p>
        </div>
      </div>

      {/* Feedback */}
      <div className="flex items-start gap-2 bg-slate-900/50 rounded-lg p-2">
        {icon}
        <p className="text-xs text-slate-300 leading-relaxed">{impact.feedback}</p>
      </div>

      {/* Meal list */}
      {impact.high_carb_meals.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {impact.high_carb_meals.map((m, i) => (
            <span key={i} className="text-[10px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">
              {m.name} ({m.carbs}g)
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
